import React, { useState, useRef, DragEvent } from 'react';
import { FileValidationOptions } from '../utils/fileValidation';

interface UseDragAndDropProps {
  onFileAccepted: (files: File[]) => void;
  validateFile: (file: File | null | undefined, options?: FileValidationOptions) => { isValid: boolean; errorMessage?: string };
  disabled?: boolean;
  currentFiles?: File[] | null;
  multiple?: boolean;
}

interface UseDragAndDropResult {
  isDraggingOver: boolean;
  handleDragEnter: (e: DragEvent<HTMLElement>) => void;
  handleDragOver: (e: DragEvent<HTMLElement>) => void;
  handleDragLeave: (e: DragEvent<HTMLElement>) => void;
  handleDrop: (e: DragEvent<HTMLElement>) => void;
  dropzoneRef: React.RefObject<HTMLElement>;
}

export const useDragAndDrop = ({
  onFileAccepted,
  validateFile,
  disabled = false,
  currentFiles = null,
  multiple = true
}: UseDragAndDropProps): UseDragAndDropResult => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dropzoneRef = useRef<HTMLElement>(null);

  const hasFiles = currentFiles && currentFiles.length > 0;

  const handleDragEnter = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasFiles && !disabled) {
      setIsDraggingOver(true);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasFiles && !disabled) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    if (hasFiles || disabled) return;

    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    const validFiles: File[] = [];

    const filesToProcess = multiple ? Array.from(droppedFiles) : [droppedFiles[0]];

    for (const file of filesToProcess) {
      const validationResult = validateFile(file);
      if (validationResult.isValid) {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      onFileAccepted(validFiles);
    }
  };

  return {
    isDraggingOver,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    dropzoneRef
  };
};
